import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { Entry } from "../../shared/snapshot-format";
import { isBookmarked, recordRecent, setBookmark, subscribePersonal } from "../character";
import { loadEntry } from "../data/entries";
import type { LinkResolver } from "../data/links";
import type { ActorContext } from "./enrich";
import { enrichDescription, readEnrichedClick } from "./enrich";
import { itemContext } from "./enrich-context";
import { featCategoryLabel, typeLabel } from "./labels";
import { Modal } from "./modal";
import { longPress } from "./long-press";
import type { RollOptions, RollRequest } from "./rolls";
import type { OpenEntryOptions } from "./sheet-context";

export interface ViewerRequest extends OpenEntryOptions {
  uuid: string;
}

interface Props {
  /** The Entry asked for; a new object pushes onto the history. Null closes the viewer. */
  request: ViewerRequest | null;
  links: LinkResolver | null;
  actor: ActorContext | null;
  onRoll: (request: RollRequest, options?: RollOptions) => void;
  onClose: () => void;
}

const ACTION_GLYPHS: Record<string, string> = { "1": "◆", "2": "◆◆", "3": "◆◆◆", reaction: "↺", free: "◇" };

/** "◆", "◆◆", "↺", "◇" for an action or feat with an action cost; "" for a passive one. */
export function actionGlyph(entry: Entry): string {
  const type = (entry.system.actionType as { value?: unknown } | undefined)?.value;
  const actions = (entry.system.actions as { value?: unknown } | undefined)?.value;
  if (type === "reaction" || type === "free") return ACTION_GLYPHS[type]!;
  if (type === "action" && typeof actions === "number") return ACTION_GLYPHS[String(actions)] ?? "";
  if (entry.type === "spell") {
    const time = (entry.system.time as { value?: unknown } | undefined)?.value;
    if (typeof time === "string") return ACTION_GLYPHS[time] ?? (time === "reaction" ? "↺" : time === "free" ? "◇" : "");
  }
  return "";
}

function describeType(entry: Entry): string {
  const category = typeof entry.system.category === "string" ? entry.system.category : null;
  const label = typeLabel(entry.type);
  if (entry.type === "feat" && category) return featCategoryLabel(category);
  if (entry.type === "spell") {
    const rank = (entry.system.level as { value?: unknown } | undefined)?.value;
    const traits = entry.traits;
    const kind = traits.includes("cantrip") ? "Cantrip" : traits.includes("focus") ? "Focus spell" : "Spell";
    return typeof rank === "number" ? `${kind} ${rank}` : kind;
  }
  if (entry.type === "familiar-ability" || category === "familiar") return "Familiar ability";
  return entry.level !== null && entry.level > 0 ? `${label} ${entry.level}` : label;
}

/**
 * The Entry viewer (issue #14): name, traits, level, rarity, Publication and the description with
 * its enrichers rendered; `@UUID` links open the target here, with Back and Forward through the
 * history; Legacy Entries are marked. Roll buttons hand their request to the page. Every Entry
 * shown joins the Recents, and the header's star marks or unmarks a Bookmark (#15).
 */
export function EntryViewer({ request, links, actor, onRoll, onClose }: Props) {
  const [history, setHistory] = useState<ViewerRequest[]>([]);
  const [index, setIndex] = useState(-1);
  const [entries, setEntries] = useState<Map<string, Entry | null>>(new Map());
  const cache = useRef(new Map<string, Promise<Entry | undefined>>());
  const [marked, setMarked] = useState(false);
  const current = index >= 0 ? history[index] : undefined;

  useEffect(() => {
    if (!request) {
      setHistory([]);
      setIndex(-1);
      return;
    }
    setHistory((h) => {
      const base = h.slice(0, index + 1);
      return [...base, request];
    });
    setIndex((i) => i + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  useEffect(() => {
    if (!current) return;
    const uuid = current.uuid;
    if (entries.has(uuid)) return;
    let p = cache.current.get(uuid);
    if (!p) {
      p = loadEntry(uuid);
      cache.current.set(uuid, p);
    }
    p.then(
      (e) => {
        setEntries((m) => new Map(m).set(uuid, e ?? null));
        if (e) recordRecent(e.uuid).catch(() => undefined);
      },
      () => setEntries((m) => new Map(m).set(uuid, null)),
    );
  }, [current, entries]);

  // The Bookmark state of the Entry shown, kept fresh when any screen changes it.
  useEffect(() => {
    const uuid = current?.uuid;
    if (!uuid) return;
    let live = true;
    const read = () => isBookmarked(uuid).then((on) => live && setMarked(on), () => undefined);
    void read();
    const off = subscribePersonal(() => void read());
    return () => {
      live = false;
      off();
    };
  }, [current?.uuid]);

  if (!request || !current) return null;
  const entry = entries.get(current.uuid);
  const title = entry ? entry.name : entry === null ? "Not available" : "Loading…";
  const toggleBookmark = () => {
    setMarked(!marked);
    setBookmark(current.uuid, !marked).catch(() => setMarked(marked));
  };
  const header = (
    <span class="viewer-nav">
      <button type="button" class="icon" aria-label="Back" disabled={index <= 0} onClick={() => setIndex(index - 1)}>‹</button>
      <button type="button" class="icon" aria-label="Forward" disabled={index >= history.length - 1} onClick={() => setIndex(index + 1)}>›</button>
      <button type="button" class={`icon star${marked ? " on" : ""}`} aria-label={marked ? "Remove Bookmark" : "Bookmark"} aria-pressed={marked} disabled={!entry} onClick={toggleBookmark}>{marked ? "★" : "☆"}</button>
    </span>
  );
  return (
    <Modal title={title} onClose={onClose} kind="panel" class="entry-viewer" header={header}>
      {entry === undefined ? <p class="muted">Loading…</p> : entry === null ? <p class="notice">This Entry is not in the Snapshot (not available offline).</p> : (
        <EntryBody entry={entry} options={current} links={links} actor={actor} onOpen={(uuid) => { setHistory([...history.slice(0, index + 1), { uuid }]); setIndex(index + 1); }} onRoll={onRoll} />
      )}
    </Modal>
  );
}

interface BodyProps {
  entry: Entry;
  options: OpenEntryOptions;
  links: LinkResolver | null;
  actor: ActorContext | null;
  onOpen: (uuid: string) => void;
  onRoll: (request: RollRequest, options?: RollOptions) => void;
}

/** One Entry's header and enriched description. Exported for the tests and for embedding elsewhere. */
export function EntryBody({ entry, options, links, actor, onOpen, onRoll }: BodyProps) {
  const html = useMemo(() => {
    const desc = entry.system.description as { value?: unknown } | undefined;
    const raw = typeof desc?.value === "string" ? desc.value : "";
    return enrichDescription(raw, { links, actor, item: itemContext(entry, { rank: options.rank, badge: options.badge }) });
  }, [entry, links, actor, options.rank, options.badge]);
  // Links and Roll buttons are delegated: a tap follows or rolls, a held press on a Roll button asks for the physical dice.
  const handlers = useMemo(
    () =>
      longPress<Element>(
        (e) => (e.target instanceof Element ? (e.target.closest("a.entry-link, button.roll") ?? null) : null),
        (el) => {
          const hit = readEnrichedClick(el);
          if (!hit) return;
          if (hit.kind === "entry") onOpen(hit.uuid);
          else onRoll(hit.request);
        },
        (el) => {
          const hit = readEnrichedClick(el);
          if (hit?.kind === "roll") onRoll(hit.request, { physical: true });
        },
      ),
    [onOpen, onRoll],
  );
  const onClick = (e: MouseEvent) => {
    if (readEnrichedClick(e.target)) e.preventDefault();
    handlers.onClick(e);
  };
  const rules = Array.isArray(entry.system.rules) ? (entry.system.rules as { key?: string }[]) : [];
  const glyph = actionGlyph(entry);
  const traditions = entry.type === "spell" ? ((entry.system.traits as { traditions?: unknown } | undefined)?.traditions as string[] | undefined) ?? [] : [];
  return (
    <article class="entry">
      <p class="entry-meta">
        <span class="entry-kind">{describeType(entry)}{glyph ? <span class="glyph" aria-label="action cost"> {glyph}</span> : null}</span>
        {!entry.publication.remaster && <span class="tag legacy">Legacy</span>}
        {entry.rarity !== "common" && <span class={`tag rarity-${entry.rarity}`}>{entry.rarity}</span>}
        {entry.traits.map((t) => <span key={t} class="tag trait">{t}</span>)}
      </p>
      {traditions.length > 0 && <p class="muted">Traditions: {traditions.join(", ")}</p>}
      <div class="desc" onClick={onClick} onPointerDown={handlers.onPointerDown} onPointerUp={handlers.onPointerUp} onPointerCancel={handlers.onPointerCancel} onPointerLeave={handlers.onPointerLeave} onPointerMove={handlers.onPointerMove} onContextMenu={handlers.onContextMenu} dangerouslySetInnerHTML={{ __html: html }} />
      <p class="entry-publication muted">
        {entry.publication.title || "Unknown publication"} · {entry.publication.license || "license unknown"} · {entry.publication.remaster ? "Remaster" : "Legacy"}
        {rules.length > 0 ? ` · Rule Elements: ${rules.map((r) => r.key ?? "?").join(", ")}` : ""}
      </p>
      <p class="entry-uuid muted">{entry.uuid}</p>
    </article>
  );
}
